import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Input, InputNumber, Label, Select, Text } from '@mercai/clever'

import {
  onErrorMessage,
  onSuccessMessage,
  useValidationErrors,
} from '@/helpers'

import { useCreateFinanceProfile, useUpdateFinanceProfile } from '@/services'
import { schema } from './form.constants'

import {
  FinanceProfileCreditGatewayEnum,
  FinanceProfileCreditStatusEnum,
  FinanceProfilePersonTypeEnum,
  FinanceProfileProps,
} from '@/types'
import { ProfileCreditFormGeneralProps } from './form.type'

import styles from './form.module.css'

const emptyRate = { days: undefined, percentage: undefined }

export const ProfileCreditFormGeneral = ({
  onHandleOpen,
  profileUuid,
  onSuccessUpdated,
  financeProfileData,
}: ProfileCreditFormGeneralProps) => {
  const { t } = useTranslation('componentsFinanceProfileForm')
  const { t: tEnums } = useTranslation('enums')
  const { t: tDefaults } = useTranslation('defaults')

  const [values, setValues] = useState<Partial<FinanceProfileProps>>({})
  const [creditInterestRatesValues, setCreditInterestRatesValues] = useState<
    {
      days?: string
      percentage?: string
    }[]
  >([])

  const { errors, isValid, onHandleListenErrors } = useValidationErrors(
    schema(tDefaults),
    values,
  )

  const onHandleValues = (key: string, value: string | number) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const { mutateAsync: mutateAsyncCreate, isPending: isLoadingCreate } =
    useCreateFinanceProfile()
  const { mutateAsync: mutateAsyncUpdate, isPending: isLoadingUpdate } =
    useUpdateFinanceProfile(profileUuid)

  const isNewRecord = !financeProfileData?.createdAt

  const validateRates = () => {
    return creditInterestRatesValues.every((rate) => {
      if (rate.days && rate.percentage) return true
      if (!rate.days && !rate.percentage) return true
      return false
    })
  }

  const onHandleSubmit = async () => {
    if (!isValid || !validateRates()) {
      onHandleListenErrors(true)
    }

    const mutateAsync = isNewRecord ? mutateAsyncCreate : mutateAsyncUpdate

    /*
      The creditInterestRates field is a JSON field 
      and has the format {DAYS: PERCENTAGE} - Record<string, string>
      Example: [{15: 0.5}, {30: 0.1}]
    */
    const parsedInterestRates = creditInterestRatesValues.reduce(
      (acc: Record<string, string>, curr) => {
        if (curr.days && curr.percentage) {
          const parsedPercentage = Number(curr.percentage) / 100
          acc[curr.days] = JSON.stringify(parsedPercentage)
        }

        return acc
      },
      {},
    )

    let payload: Partial<FinanceProfileProps> = {
      creditStatus: values.creditStatus,
      creditGateway: values.creditGateway,
      totalCreditAmount: values.totalCreditAmount,
      personType: values.personType,
      creditBureau:
        values.creditGateway === 'mercai_cash' ? 'black_trust' : undefined,
      creditBureauUrl:
        values.creditGateway === 'mercai_cash'
          ? values.creditBureauUrl
          : undefined,
      creditInterestRates: parsedInterestRates,
    }

    if (isNewRecord) {
      payload = {
        ...payload,
        profileUuid,
        upfrontPaymentPercentage: values?.upfrontPaymentPercentage || 1,
      }
    }

    const response = await mutateAsync(payload)

    if (response.success) {
      onSuccessMessage(
        isNewRecord
          ? t('profileCreditSuccessTitle')
          : t('profileCreditSuccessUpdateTitle'),
        isNewRecord
          ? t('profileCreditSuccessUpdateMessage')
          : t('profileCreditSuccessUpdateMessage'),
      )

      if (!isNewRecord) {
        onHandleOpen(false)
      }

      if (onSuccessUpdated) {
        onSuccessUpdated()
      }

      onHandleListenErrors(false)
    } else {
      onErrorMessage(
        t('profileCreditErrorTitle'),
        t('profileCreditErrorUpdateMessage'),
      )
    }
  }

  const handleChangeDeadline = ({
    index,
    days,
    percentage,
  }: {
    index: number
    days?: string
    percentage?: string
  }) => {
    const newValues = [...creditInterestRatesValues]

    if (days) {
      newValues[index] = { ...newValues[index], days }
    }

    if (percentage) {
      newValues[index] = { ...newValues[index], percentage }
    }

    setCreditInterestRatesValues(newValues)
  }

  const onAddDeadline = () => {
    const newValues = [...creditInterestRatesValues]
    newValues.push(emptyRate)
    setCreditInterestRatesValues(newValues)
  }

  useEffect(() => {
    const financeProfileDataInterestRates =
      financeProfileData?.creditInterestRates || {}

    if (financeProfileData) {
      setValues(financeProfileData)
    }

    if (Object.keys(financeProfileDataInterestRates).length > 0) {
      const interestRates = Object.keys(financeProfileDataInterestRates)?.map(
        (rateKey) => {
          const percentageValue =
            Number(financeProfileData?.creditInterestRates?.[rateKey]) * 100

          return {
            days: rateKey,
            percentage: JSON.stringify(percentageValue),
          }
        },
      )

      setCreditInterestRatesValues(interestRates)
    } else {
      setCreditInterestRatesValues([emptyRate])
    }
  }, [financeProfileData])

  return (
    <div className={styles.core}>
      <div className={styles.content}>
        <div>
          <Label>{t('profileCreditStatusLabel')}</Label>
          <Select
            options={Object.keys(FinanceProfileCreditStatusEnum).map(
              (financeProfileKey) => ({
                label: tEnums(`profile_credit_status_${financeProfileKey}`),
                value: financeProfileKey,
              }),
            )}
            value={values.creditStatus}
            onChange={(value) => onHandleValues('creditStatus', value)}
            placeholder={t('profileCreditStatusPlaceholder')}
            error={errors?.creditStatus as string}
          />
        </div>

        <div>
          <Label>{t('profileCreditGatewayLabel')}</Label>
          <Select
            options={Object.keys(FinanceProfileCreditGatewayEnum).map(
              (financeProfileKey) => ({
                label: tEnums(`profile_credit_gateway_${financeProfileKey}`),
                value: financeProfileKey,
              }),
            )}
            value={values.creditGateway}
            onChange={(value) => onHandleValues('creditGateway', value)}
            placeholder={t('profileCreditGatewayPlaceholder')}
            error={errors?.creditGateway as string}
          />
        </div>

        {values.creditGateway === 'mercai_cash' && (
          <div>
            <Label>{t('profileCreditCreditBureauUrlLabel')}</Label>
            <Input
              value={values.creditBureauUrl}
              onChange={(value) => onHandleValues('creditBureauUrl', value)}
              placeholder={t('profileCreditCreditBureauUrlPlaceholder')}
              error={errors?.creditBureauUrl as string}
            />
          </div>
        )}

        <div>
          <Label>{t('profileCreditTotalCreditAmountLabel')}</Label>
          <InputNumber
            value={values.totalCreditAmount}
            onChange={(value) => onHandleValues('totalCreditAmount', value)}
            min={0}
            prefix="$ "
            showIcons
            placeholder={t('profileCreditTotalCreditAmountPlaceholder')}
            error={errors?.totalCreditAmount as string}
          />
        </div>

        <div>
          <Label>{t('profileCreditPersonTypeLabel')}</Label>
          <Select
            options={Object.keys(FinanceProfilePersonTypeEnum).map(
              (financeProfileKey) => ({
                label: tEnums(
                  `profile_credit_person_type_${financeProfileKey}`,
                ),
                value: financeProfileKey,
              }),
            )}
            value={values.personType}
            onChange={(value) => onHandleValues('personType', value)}
            placeholder={t('profileCreditPersonTypePlaceholder')}
            error={errors?.personType as string}
          />
        </div>

        <div>
          <Text variant="title-medium" className={styles.greened}>
            {t('profileCreditCreditInterestRatesTitle')}
          </Text>

          <div className={styles['deadline-item']}>
            <div>
              <Text variant="text-medium">
                {t('profileCreditCreditInterestDaysLabel')}
              </Text>
            </div>

            <div>
              <Text variant="text-medium">
                {t('profileCreditCreditInterestPercentageLabel')}
              </Text>
            </div>
          </div>

          {creditInterestRatesValues.map((deadline, index) => {
            return (
              <div className={styles['deadline-item']}>
                <div>
                  <Select
                    options={[
                      {
                        label: `15 ${t('profileCreditDaysOptionsLabel')}`,
                        value: '15',
                      },
                      {
                        label: `30 ${t('profileCreditDaysOptionsLabel')}`,
                        value: '30',
                      },
                      {
                        label: `45 ${t('profileCreditDaysOptionsLabel')}`,
                        value: '45',
                      },
                      {
                        label: `60 ${t('profileCreditDaysOptionsLabel')}`,
                        value: '60',
                      },
                      {
                        label: `90 ${t('profileCreditDaysOptionsLabel')}`,
                        value: '90',
                      },
                    ]}
                    value={deadline.days}
                    onChange={(value) =>
                      handleChangeDeadline({ index, days: value })
                    }
                    error={errors?.personType as string}
                  />
                </div>

                <div>
                  <InputNumber
                    decimalPrecision={2}
                    stepSize={1}
                    showIcons
                    suffix="%"
                    className={styles['input-width-100']}
                    value={deadline.percentage}
                    onChange={(value) =>
                      handleChangeDeadline({
                        index,
                        percentage: JSON.stringify(value),
                      })
                    }
                  />
                </div>
              </div>
            )
          })}

          <Button
            variant="ghost"
            size="small"
            onClick={onAddDeadline}
            className={styles['add-button']}
          >
            {t('profileCreditCreditInterestAddButtonLabel')}
          </Button>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={() => onHandleOpen(false)}>
          {t('profileCreditButtonClose')}
        </Button>
        <Button onClick={onHandleSubmit}>
          {isLoadingUpdate || isLoadingCreate
            ? t('profileCreditButtonSubmitLoading')
            : t('profileCreditButtonSubmit')}
        </Button>
      </div>
    </div>
  )
}
